// ============================================================================
// MANPOWER ATTENDANCE VIEW - WITH DATE NAVIGATION
// Path: components/dashboard/pages/components/ManpowerAttendanceView.tsx
// ============================================================================

import React, { useState, useEffect } from 'react';
import {
  manpowerAttendanceService,
  AttendanceRecord
} from '../../../../services/manpowerAttendance.service';
import { ManpowerEmployee } from '../../../../services/manpower.service';
import styles from './ManpowerAttendanceView.module.css';

interface ManpowerAttendanceViewProps {
  employees: ManpowerEmployee[];
  projectName: string;
}

type FilterType = 'all' | 'present' | 'absent';

const ManpowerAttendanceView: React.FC<ManpowerAttendanceViewProps> = ({
  employees,
  projectName
}) => {
  const [loading, setLoading] = useState(false);
  const [attendanceData, setAttendanceData] = useState<Map<string, AttendanceRecord>>(new Map());
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<FilterType>('all');
  
  // Date management
  const [selectedDate, setSelectedDate] = useState<string>(getTodayDate());
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Load attendance when date or employees change
  useEffect(() => {
    if (employees.length > 0) {
      loadAttendanceForDate(selectedDate);
    }
  }, [employees, selectedDate]);

  // Get today's date in YYYY-MM-DD format
  function getTodayDate(): string {
    const today = new Date();
    return today.toISOString().split('T')[0];
  }

  // Get yesterday's date
  function getYesterdayDate(): string {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  }

  // Load attendance for specific date
  const loadAttendanceForDate = async (date: string) => {
    setLoading(true);
    try {
      const employeeList = employees.map(emp => ({
        empCode: emp.empNumber,
        employeeName: emp.name
      }));

      const attendance = await manpowerAttendanceService.getAttendanceForDate(
        employeeList,
        date
      );

      setAttendanceData(attendance);
    } catch (error) {
      console.error('Error loading attendance:', error);
    } finally {
      setLoading(false);
    }
  };

  // Date navigation handlers
  const handleTodayClick = () => {
    setSelectedDate(getTodayDate());
  };

  const handleYesterdayClick = () => {
    setSelectedDate(getYesterdayDate());
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(e.target.value);
    setShowDatePicker(false);
  };

  // Calculate statistics
  const calculateStats = () => {
    let totalPresent = 0;
    let totalAbsent = 0;

    attendanceData.forEach(record => {
      if (record.status === 'Present') {
        totalPresent++;
      } else {
        totalAbsent++;
      }
    });

    return {
      totalEmployees: employees.length,
      totalPresent,
      totalAbsent,
      attendancePercentage: employees.length > 0 
        ? Math.round((totalPresent / employees.length) * 100) 
        : 0
    };
  };

  const stats = calculateStats();

  // Filter and search employees
  const filteredEmployees = employees.filter(emp => {
    const attendance = attendanceData.get(emp.empNumber);
    const isPresent = attendance?.status === 'Present';

    // Apply status filter
    if (filter === 'present' && !isPresent) return false;
    if (filter === 'absent' && isPresent) return false;

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        emp.name.toLowerCase().includes(query) ||
        emp.empCode.toLowerCase().includes(query) ||
        emp.empNumber.toLowerCase().includes(query) ||
        (emp.projectCode && emp.projectCode.toLowerCase().includes(query))
      );
    }

    return true;
  });

  // Format date for display
  const formatDisplayDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Check if selected date is today
  const isToday = selectedDate === getTodayDate();
  const isYesterday = selectedDate === getYesterdayDate();

  return (
    <div className={styles.container}>
      {/* Header with Date Navigation */}
      <div className={styles.header}>
        <div>
          <h2>Attendance Report</h2>
          <p className={styles.projectName}>{projectName}</p>
        </div>
        
        {/* Simple inline stats */}
        <div className={styles.inlineStats}>
          <span className={styles.statText}>
            Total: <strong>{stats.totalEmployees}</strong>
          </span>
          <span className={`${styles.statText} ${styles.presentText}`}>
            Present: <strong>{stats.totalPresent}</strong>
          </span>
          <span className={`${styles.statText} ${styles.absentText}`}>
            Absent: <strong>{stats.totalAbsent}</strong>
          </span>
          <span className={styles.statText}>
            Rate: <strong>{stats.attendancePercentage}%</strong>
          </span>
        </div>
      </div>

      {/* Date Navigation Bar */}
      <div className={styles.dateNavBar}>
        <div className={styles.dateButtons}>
          <button
            className={isToday ? styles.dateButtonActive : styles.dateButton}
            onClick={handleTodayClick}
          >
            📅 Today
          </button>
          <button
            className={isYesterday ? styles.dateButtonActive : styles.dateButton}
            onClick={handleYesterdayClick}
          >
            📆 Yesterday
          </button>
          <button
            className={styles.dateButton}
            onClick={() => setShowDatePicker(!showDatePicker)}
          >
            📋 Custom Date
          </button>
        </div>

        <div className={styles.selectedDateDisplay}>
          <span className={styles.dateLabel}>Viewing:</span>
          <span className={styles.dateValue}>{formatDisplayDate(selectedDate)}</span>
        </div>
      </div>

      {/* Custom Date Picker (appears when clicked) */}
      {showDatePicker && (
        <div className={styles.datePickerRow}>
          <label>Select Date:</label>
          <input
            type="date"
            value={selectedDate}
            onChange={handleDateChange}
            max={getTodayDate()}
            className={styles.dateInput}
          />
        </div>
      )}

      {/* Filter and Search Bar */}
      <div className={styles.controlBar}>
        <div className={styles.filterButtons}>
          <button
            className={filter === 'all' ? styles.filterButtonActive : styles.filterButton}
            onClick={() => setFilter('all')}
          >
            All ({stats.totalEmployees})
          </button>
          <button
            className={filter === 'present' ? styles.filterButtonActive : styles.filterButton}
            onClick={() => setFilter('present')}
          >
            Present ({stats.totalPresent})
          </button>
          <button
            className={filter === 'absent' ? styles.filterButtonActive : styles.filterButton}
            onClick={() => setFilter('absent')}
          >
            Absent ({stats.totalAbsent})
          </button>
        </div>

        <input
          type="text"
          placeholder="Search by name, code, or PIN..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={styles.searchInput}
        />
      </div>

      {/* Excel-like Table */}
      {loading ? (
        <div className={styles.loadingState}>
          <div className={styles.spinner}></div>
          <p>Loading attendance data...</p>
        </div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>S.NO</th>
                <th>Employee Name</th>
                <th>Code</th>
                <th>PIN</th>
                <th>Project Code</th>
                <th>Site</th>
                <th>Position</th>
                <th>Status</th>
                <th>Check In</th>
                <th>Check Out</th>
                <th>Location</th>
              </tr>
            </thead>
            <tbody>
              {filteredEmployees.length > 0 ? (
                filteredEmployees.map((employee, index) => {
                  const attendance = attendanceData.get(employee.empNumber);
                  const isPresent = attendance?.status === 'Present';

                  return (
                    <tr key={employee.empCode} className={isPresent ? styles.presentRow : styles.absentRow}>
                      <td>{index + 1}</td>
                      <td className={styles.nameCell}>{employee.name}</td>
                      <td>{employee.empCode}</td>
                      <td>{employee.empNumber.replace(/^EMP/i, '')}</td>
                      <td>{employee.projectCode || '-'}</td>
                      <td>{employee.site}</td>
                      <td>{employee.position}</td>
                      <td>
                        <span className={isPresent ? styles.statusPresent : styles.statusAbsent}>
                          {isPresent ? '✓ Present' : '✗ Absent'}
                        </span>
                      </td>
                      <td className={styles.timeCell}>{attendance?.checkInTime || '-'}</td>
                      <td className={styles.timeCell}>{attendance?.checkOutTime || '-'}</td>
                      <td className={styles.locationCell}>
                        {attendance?.locationName || attendance?.location || '-'}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={11} className={styles.emptyCell}>
                    {searchQuery 
                      ? `No employees found matching "${searchQuery}"`
                      : 'No employees to display'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Summary Footer */}
          {filteredEmployees.length > 0 && (
            <div className={styles.tableSummary}>
              Showing {filteredEmployees.length} of {employees.length} employees
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ManpowerAttendanceView;